import CioAutocomplete from './constructorio-ui-autocomplete-bundled.js';
import { formatPrice, hiddenPriceFields, pricesForMarket } from './constructor-price.js';

const numericAmount = (amount) => {
    if (amount == null || amount === '') return NaN;

    const value = Array.isArray(amount) ? Number(amount[0]) : Number(amount);

    return Number.isFinite(value) ? value : NaN;
};

const renderProductItem = ({ item }) => {
    const data = item?.data || {};
    const title = item?.value || '';
    const url = data.url || '#';
    const imageUrl = data.image_url || '';
    const countryCode = window.theme?.countryCode || 'US';
    const currencyCode = window.theme?.currency || 'USD';
    const marketPrices = pricesForMarket(data, countryCode);
    const priceValue = numericAmount(marketPrices.price);
    const compareAtValue = numericAmount(marketPrices.compare_at_price);
    const priceLabel = formatPrice(priceValue, { countryCode, currencyCode });
    const capLabel = formatPrice(compareAtValue, { countryCode, currencyCode });
    const onSale = Boolean(capLabel) && compareAtValue > priceValue;

    const anchor = document.createElement('a');
    anchor.href = url;

    if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'cio-product-image';
        image.src = imageUrl;
        image.alt = title;
        image.setAttribute('data-testid', 'cio-img');
        anchor.append(image);
    }

    const info = document.createElement('div');
    info.className = 'cio-product-info';

    const text = document.createElement('p');
    text.className = 'cio-product-text';
    text.setAttribute('data-testid', 'cio-text');
    text.textContent = title;
    info.append(text);

    if (priceLabel) {
        const price = document.createElement('div');
        price.className = 'cio-product-price';

        const compareAt = document.createElement('s');
        compareAt.className = 'cio-product-cap';

        if (onSale) {
            compareAt.textContent = capLabel;
        } else {
            compareAt.hidden = true;
        }

        const amount = document.createElement('span');
        amount.className = 'cio-product-amount';
        amount.textContent = priceLabel;

        price.append(compareAt, amount);
        info.append(price);
    }

    anchor.append(info);

    return anchor;
};

const isResultsOpen = (results) => {
    if (!results) return false;

    const style = results.getAttribute('style') || '';

    if (style.includes('display: none') || style.includes('display:none')) return false;

    return getComputedStyle(results).display !== 'none';
};

const closeAutocomplete = (mount) => {
    const closeIcon = mount.querySelector('.cio-close');

    if (closeIcon) {
        closeIcon.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        return;
    }

    const input = mount.querySelector('.cio-input');

    input?.blur();
};

const searchForLabel = (template, terms) => {
    if (template.includes('[[terms]]')) {
        return template.replace('[[terms]]', terms);
    }

    return `Search for “${terms}”`;
};

// The results list is owned by Constructor. On desktop, the query link sits under
// the suggestions when that list has items. Otherwise it stays at the end of the
// results, outside the scrolling product list.
const bindSearchQueryButton = (mount, searchUrl) => {
    const template = mount.dataset.searchFor || 'Search for “[[terms]]”';
    const desktopResults = window.matchMedia('(min-width: 1024px)');
    const item = document.createElement('div');
    const button = document.createElement('button');
    const label = document.createElement('span');

    item.className = 'header-search--query';
    button.type = 'button';
    button.className = 'header-search--query-button';
    label.className = 'underline-dashed';
    button.append(label);
    item.append(button);

    const sync = () => {
        const results = mount.querySelector('.cio-results');
        const query = mount.querySelector('.cio-input')?.value.trim() ?? '';
        const open = query.length > 0 && isResultsOpen(results);

        if (!open || !results) {
            item.remove();
            return;
        }

        const nextLabel = searchForLabel(template, query);

        if (label.textContent !== nextLabel) {
            label.textContent = nextLabel;
        }

        const suggestions = results.querySelector('.cio-section-search-suggestions');
        const suggestionList = suggestions?.querySelector('.cio-section-items');
        const placeUnderSuggestions = desktopResults.matches && Boolean(suggestionList?.querySelector('.cio-item'));

        if (placeUnderSuggestions) {
            if (item.parentElement !== suggestions || suggestionList.nextElementSibling !== item) {
                suggestionList.after(item);
            }

            return;
        }

        if (item.parentElement !== results || results.lastElementChild !== item) {
            results.append(item);
        }
    };

    const submitQuery = () => {
        const form = mount.querySelector('.cio-form');

        if (form && typeof form.requestSubmit === 'function') {
            form.requestSubmit();
            return;
        }

        const query = mount.querySelector('.cio-input')?.value.trim() ?? '';

        if (!query) {
            return;
        }

        window.location.assign(buildFreshSearchUrl(query, searchUrl));
    };

    button.addEventListener('mousedown', (event) => {
        event.preventDefault();
    });

    button.addEventListener('click', (event) => {
        event.preventDefault();
        submitQuery();
    });

    mount.addEventListener('input', sync);
    desktopResults.addEventListener('change', sync);

    const observer = new MutationObserver(sync);

    observer.observe(mount, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
    });

    sync();
};

const bindDesktopCloseButton = (mount) => {
    const container = mount.closest('.header-search--desktop');
    const closeButton = container?.querySelector('[data-header-search-close]');

    if (!container || !closeButton) return;

    const syncCloseButton = () => {
        const open = isResultsOpen(mount.querySelector('.cio-results'));

        closeButton.hidden = !open;
    };

    closeButton.addEventListener('click', () => {
        closeAutocomplete(mount);
        syncCloseButton();
    });

    const observer = new MutationObserver(syncCloseButton);

    observer.observe(mount, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
    });

    syncCloseButton();
};

const isAutocompleteItem = (submit) => submit != null && 'item' in submit && 'originalQuery' in submit;

const searchTermFromSubmit = (submit) => {
    if (isAutocompleteItem(submit)) {
        if (submit.item?.section === 'Search Suggestions') {
            return submit.item.value || '';
        }

        return '';
    }

    return submit?.query || '';
};

// Constructor's Shopify default copies the current query string onto the next search,
// which keeps color filters and sort from the previous results.
const shouldDropSearchParam = (key) =>
    key === 'page' || key === 'sort_by' || key === 'section_id' || key.startsWith('filter.');

const buildFreshSearchUrl = (query, searchUrl) => {
    const url = new URL(searchUrl || '/search', window.location.origin);
    const params = new URLSearchParams();

    for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
        if (shouldDropSearchParam(key)) {
            continue;
        }

        params.append(key, value);
    }

    params.set('q', query);
    url.search = params.toString();

    return url.toString();
};

const navigateToProduct = (productUrl) => {
    if (!productUrl) {
        return;
    }

    let url;

    try {
        url = new URL(productUrl);
    } catch {
        url = new URL(productUrl, window.location.origin);
    }

    new URLSearchParams(window.location.search).forEach((value, key) => {
        if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
        }
    });

    window.location.assign(url.toString());
};

const onAutocompleteSubmit = (submit, searchUrl) => {
    if (isAutocompleteItem(submit) && submit.item?.section === 'Products') {
        navigateToProduct(submit.item?.data?.url);
        return;
    }

    const query = searchTermFromSubmit(submit).trim();

    if (!query) {
        return;
    }

    window.location.assign(buildFreshSearchUrl(query, searchUrl));
};

const initConstructorAutocomplete = () => {
    const apiKey = window.cnstrc?.indexKey;

    if (!apiKey) return;

    const mounts = document.querySelectorAll('[data-constructor-autocomplete]');

    if (mounts.length === 0) return;

    const placeholder = mounts[0].dataset.placeholder || 'Search';
    const searchUrl = window.routes?.search_url || '/search';

    for (const mount of mounts) {
        if (!mount.id || mount.querySelector('.cio-autocomplete')) continue;

        const options = {
            selector: `#${CSS.escape(mount.id)}`,
            apiKey,
            placeholder,
            useShopifyDefaults: true,
            onSubmit: (submit) => onAutocompleteSubmit(submit, searchUrl),
            shopifySettings: { searchUrl },
            advancedParameters: {
                hiddenFields: hiddenPriceFields(),
            },
            sections: [
                { indexSectionName: 'Search Suggestions', type: 'autocomplete' },
                {
                    indexSectionName: 'Products',
                    type: 'autocomplete',
                    renderItem: renderProductItem,
                },
            ],
        };

        if (window.cnstrc.userId) {
            options.cioClientOptions = { userId: String(window.cnstrc.userId) };
        }

        CioAutocomplete(options);
        bindSearchQueryButton(mount, searchUrl);
        bindDesktopCloseButton(mount);
    }
};

initConstructorAutocomplete();
